<script setup>
import Buttons from '@/components/ui/Buttons.vue';
import InfoStatCard from '@/components/cards/InfoStatCard.vue';
import MarkCard from '@/components/cards/MarkCard.vue';

defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data?.background_color}` : '',
      data?.spacing?.top
        ? `section--top-${data.spacing.top}`
        : 'section--top-xl',
      data?.spacing?.bottom
        ? `section--bottom-${data.spacing.bottom}`
        : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        class="info-stat"
        :class="[data?.background_color === 'black' ? 'white' : 'black']"
      >
        <div class="info-stat__top max-w-784">
          <NuxtImg
            v-if="data?.image?.src"
            :src="data?.image?.src"
            :alt="data?.image?.alt"
            :width="data?.image?.width"
            :height="data?.image?.height"
            class="info-stat__image"
            data-aos="zoom-in"
            data-aos-easing="ease-out-quad"
            loading="lazy"
          />
          <div class="info-stat__content">
            <h2
              v-if="data.title"
              class="mb-0 info-stat__title h2 text-center"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="data.title"
            ></h2>
            <div
              v-if="data.text"
              class="mb-0 info-stat__text body-1-regular text-center"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="data.text"
            ></div>
          </div>
        </div>
        <div
          ref="wrap"
          :class="[`info-stat__items info-stat__items--${data?.columns_count}`]"
        >
          <InfoStatCard
            v-for="(item, index) in data.items"
            :key="index"
            :title="item?.title"
            :value="item?.value"
            :background-color="data?.background_color"
            :view="data?.template"
            data-aos="fade-up"
            :data-aos-delay="index >= 1 ? 100 + 100 * index : 100"
          />
        </div>
        <div
          v-if="
            data?.marks &&
            (data?.marks?.title || data?.marks?.items?.length > 0)
          "
          class="info-stat__marks"
        >
          <p
            v-if="data?.marks?.title"
            class="info-stat__marks-title body-1-regular"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data?.marks?.title"
          ></p>
          <div class="info-stat__marks-items">
            <MarkCard
              v-for="(mark, markIndex) in data?.marks?.items"
              :key="markIndex"
              :icon="mark?.icon"
              :title="mark?.title"
              :value="mark?.value"
              :background-color="data?.background_color"
              data-aos="fade-up"
              :data-aos-delay="markIndex >= 1 ? 100 + 100 * markIndex : 100"
            />
          </div>
        </div>
        <div
          v-if="data?.note || data?.buttons"
          class="info-stat__bottom max-w-784"
        >
          <Buttons
            v-if="data?.buttons && data?.buttons.length > 0"
            class="info-stat__buttons"
            :buttons="data?.buttons"
            :background-color="data?.background_color"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-info-stat'"
          />
          <div
            v-if="data?.note"
            class="info-stat__note body-3-regular"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data?.note"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.info-stat {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin: 0 auto;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
      margin: 0 auto;
    }

    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__image {
    border-radius: 4px;
    margin: 0 auto;
    height: auto;
    width: 45%;
    @include media-breakpoint-up(sm) {
      width: 23%;
    }
    @include media-breakpoint-up(md) {
      width: 21.5%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &__title {
    margin: 0 auto;
    @include media-breakpoint-up(sm) {
      max-width: 80.5%;
    }
    @include media-breakpoint-up(md) {
      max-width: unset;
    }
  }
  &__text {
    margin: 0 auto;
    &:deep(p:last-child) {
      margin-bottom: 0;
    }
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      max-width: 80.5%;
    }
    @include media-breakpoint-up(md) {
      max-width: 74.5%;
    }
  }
  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    &--centered {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(md) {
      &--3 {
        grid-template-columns: repeat(3, 1fr);
      }
      &--4 {
        grid-template-columns: repeat(4, 1fr);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__marks {
    display: flex;
    flex-direction: column;
    gap: 32px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    &-title {
      margin: 0 auto;
    }
    &-items {
      display: grid;
      column-gap: 8px;
      grid-template-columns: repeat(3, 1fr);
      row-gap: 16px;
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(5, 1fr);
        row-gap: 24px;
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(9, 1fr);
      }
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 48px;
    margin: 0 auto;
  }
  &__note {
    color: $on-surface-variant-2;
    text-align: center;
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      max-width: 80.5%;
    }
    @include media-breakpoint-up(md) {
      max-width: 74.5%;
    }
    &:deep(p:last-child) {
      margin-bottom: 0;
    }
  }
  &__buttons {
    justify-content: center;
  }
}
</style>
